/*
 * Logo check engine (spec 03 R-09 to R-16, ARCHITECTURE §9).
 * Runs once when a logo is uploaded. The same code runs in the admin panel and
 * the phone (preview, from canvas pixels) and on the server (from libvips pixels),
 * so both always agree. Ported from prototype/vouchers/src/06-logo.js.
 *
 * Steps: type + size → background → trim margins → shape → print sharpness →
 * light-logo tile → slot sizes. Step keys match the i18n strings (lgType, …).
 */

export const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'] as const;
export type LogoType = (typeof LOGO_TYPES)[number];
export const LOGO_MAX_BYTES = 5 * 1024 * 1024;
/** Longest side the check works at; bigger images are scaled down first. */
export const WORK_SIZE = 1600;

export type LogoShape = 'tall' | 'square' | 'wide' | 'bar';
export type LogoError = 'type' | 'size' | 'read' | 'empty';

export interface Step {
  ok: boolean;
  k: 'lgType' | 'lgBgRemoved' | 'lgBgKept' | 'lgBgClear' | 'lgTrimmed' | 'lgNoTrim' | 'lgShape'
    | 'lgVector' | 'lgSharp' | 'lgBlurry' | 'lgLight' | 'lgContrast' | 'lgFits';
  a?: (string | number)[];
}

/** RGBA pixels, 4 bytes per pixel, rows top to bottom (canvas ImageData and sharp raw both fit). */
export interface Raster {
  data: Uint8Array | Uint8ClampedArray;
  width: number;
  height: number;
}

export interface FileMeta {
  type: string;
  bytes: number;
  /** The file's own pixel size; the raster may be scaled down from it. */
  naturalWidth?: number;
  naturalHeight?: number;
}

export interface LogoResult {
  /** Trimmed, padded copy with the background removed (RGBA). */
  fixed: Raster;
  ratio: number;
  shape: LogoShape;
  tile: boolean;
  vector: boolean;
  /** Print resolution at the A4 slot height; null for vector files. */
  dpi: number | null;
  steps: Step[];
  warn: ('blurry' | 'light')[];
}

export type CheckOutcome = { error: LogoError } | LogoResult;

const label = (type: string) =>
  type.split('/')[1]!.replace('svg+xml', 'SVG').replace('jpeg', 'JPG').toUpperCase();

/** Step 1, before any decoding: type and size (R-10). */
export function checkFile(meta: FileMeta): { error: LogoError } | { step: Step } {
  if (!(LOGO_TYPES as readonly string[]).includes(meta.type)) return { error: 'type' };
  if (meta.bytes > LOGO_MAX_BYTES) return { error: 'size' };
  return { step: { ok: true, k: 'lgType', a: [label(meta.type), Math.max(1, Math.round(meta.bytes / 1024))] } };
}

/** R-13: shape by width ÷ height after trimming. */
export function logoShape(ratio: number): LogoShape {
  return ratio < 0.8 ? 'tall' : ratio <= 1.6 ? 'square' : ratio <= 3.6 ? 'wide' : 'bar';
}

/** R-16: every logo gets about the same visual area, never cropped or stretched (mm). */
export function slotSize(ratio: number, H: number, maxW: number): { w: number; h: number } {
  let w = Math.sqrt(H * H * ratio);
  let h = w / ratio;
  if (h > H) { h = H; w = h * ratio; }
  if (h < H * 0.45) { h = H * 0.45; w = h * ratio; }
  if (w > maxW) { w = maxW; h = w / ratio; }
  return { w, h };
}

/** Slot limits in mm: A4 header, landscape header, small headers (R-16). */
export const SLOTS = { a4: [17, 60], landscape: [12, 44], small: [10, 36] } as const;

/** A long-bar logo sits on its own line above the company name (R-13). */
export const logoStacks = (ratio: number) => ratio > 3.6;

/** The scale factor and size the check works at for an image of this size. */
export function workSize(width: number, height: number): { k: number; width: number; height: number } {
  const k = Math.min(1, WORK_SIZE / Math.max(width, height));
  return { k, width: Math.max(1, Math.round(width * k)), height: Math.max(1, Math.round(height * k)) };
}

/**
 * Steps 2–7 on decoded pixels (already at workSize). Doesn't change `raster`.
 */
export function analyse(raster: Raster, meta: FileMeta): CheckOutcome {
  const file = checkFile(meta);
  if ('error' in file) return file;
  const { width: W, height: H } = raster;
  if (W < 1 || H < 1 || raster.data.length < W * H * 4) return { error: 'read' };
  const d = Uint8ClampedArray.from(raster.data);
  const steps: Step[] = [file.step];
  const warn: LogoResult['warn'] = [];
  const vector = meta.type === 'image/svg+xml';
  const px = (x: number, y: number) => (y * W + x) * 4;

  // 2. background (R-11)
  const corners = [[0, 0], [W - 1, 0], [0, H - 1], [W - 1, H - 1]].map(([x, y]) => {
    const i = px(x!, y!);
    return [d[i]!, d[i + 1]!, d[i + 2]!, d[i + 3]!] as const;
  });
  if (corners.every(c => c[3] < 20)) {
    steps.push({ ok: true, k: 'lgBgClear' });
  } else {
    const ref = corners[0]!;
    const flat = corners.every(c => Math.abs(c[0] - ref[0]) + Math.abs(c[1] - ref[1]) + Math.abs(c[2] - ref[2]) < 30);
    const light = (ref[0] + ref[1] + ref[2]) / 3 > 215;
    if (flat && light) {
      // flood fill from the border, so white inside the logo stays
      const near = (i: number) =>
        Math.abs(d[i]! - ref[0]) + Math.abs(d[i + 1]! - ref[1]) + Math.abs(d[i + 2]! - ref[2]) <= 60 && d[i + 3]! > 200;
      const seen = new Uint8Array(W * H);
      const stack: number[] = [];
      for (let x = 0; x < W; x++) stack.push(x, (H - 1) * W + x);
      for (let y = 0; y < H; y++) stack.push(y * W, y * W + W - 1);
      while (stack.length) {
        const p = stack.pop()!;
        if (seen[p]) continue;
        seen[p] = 1;
        const i = p * 4;
        if (!near(i)) continue;
        d[i + 3] = 0;
        const x = p % W, y = (p / W) | 0;
        if (x > 0) stack.push(p - 1);
        if (x < W - 1) stack.push(p + 1);
        if (y > 0) stack.push(p - W);
        if (y < H - 1) stack.push(p + W);
      }
      steps.push({ ok: true, k: 'lgBgRemoved' });
    } else {
      steps.push({ ok: true, k: 'lgBgKept' });
    }
  }

  // 3. trim empty margins (alpha ≤ 24) and measure brightness (R-12, R-15)
  let x0 = W, y0 = H, x1 = -1, y1 = -1, sum = 0, n = 0;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const i = px(x, y);
      const al = d[i + 3]!;
      if (al > 24) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
        const a = al / 255;
        sum += (a * (0.2126 * d[i]! + 0.7152 * d[i + 1]! + 0.0722 * d[i + 2]!)) / 255;
        n += a;
      }
    }
  }
  if (x1 < 0) return { error: 'empty' };
  const tw = x1 - x0 + 1, th = y1 - y0 + 1;
  const trimmed = 1 - (tw * th) / (W * H);
  steps.push({ ok: true, k: trimmed > 0.04 ? 'lgTrimmed' : 'lgNoTrim', a: [Math.round(trimmed * 100)] });

  // 3 % safe padding added back
  const pad = Math.round(Math.max(tw, th) * 0.03);
  const fw = tw + pad * 2, fh = th + pad * 2;
  const out = new Uint8ClampedArray(fw * fh * 4);
  for (let y = 0; y < th; y++) {
    const from = px(x0, y0 + y);
    out.set(d.subarray(from, from + tw * 4), ((y + pad) * fw + pad) * 4);
  }

  // 4. shape (R-13)
  const ratio = fw / fh;
  const shape = logoShape(ratio);
  steps.push({ ok: true, k: 'lgShape', a: [shape, ratio.toFixed(1)] });

  // 5. print sharpness at the A4 slot height (R-14)
  const a4 = slotSize(ratio, ...SLOTS.a4);
  const scale = meta.naturalHeight ? meta.naturalHeight / H : 1;
  const dpi = vector ? null : Math.round((th * scale) / (a4.h / 25.4));
  if (vector) steps.push({ ok: true, k: 'lgVector' });
  else if (dpi! >= 200) steps.push({ ok: true, k: 'lgSharp', a: [dpi!] });
  else {
    steps.push({ ok: false, k: 'lgBlurry', a: [dpi!, Math.ceil((a4.w / 25.4) * 300)] });
    warn.push('blurry');
  }

  // 6. light logos go on a brand-colour tile (R-15)
  const tile = (n ? sum / n : 0) > 0.82;
  if (tile) {
    steps.push({ ok: false, k: 'lgLight' });
    warn.push('light');
  } else steps.push({ ok: true, k: 'lgContrast' });

  // 7. the size it prints at
  const land = slotSize(ratio, ...SLOTS.landscape);
  steps.push({ ok: true, k: 'lgFits', a: [a4.w.toFixed(0), a4.h.toFixed(0), land.w.toFixed(0), land.h.toFixed(0)] });

  return { fixed: { data: out, width: fw, height: fh }, ratio, shape, tile, vector, dpi, steps, warn };
}

/**
 * SVG files are drawn by the server, so anything that could run or fetch is refused
 * (scripts, event handlers, foreign objects, entities, outside links).
 */
export function svgIsSafe(svg: string): boolean {
  return !/<script|\bon[a-z]+\s*=|<foreignObject|<!ENTITY|<!DOCTYPE|<iframe|<embed|<object|javascript:/i.test(svg)
    && !/(?:xlink:)?href\s*=\s*["'](?!#|data:image\/(?:png|jpeg|webp);base64,)/i.test(svg)
    && !/url\(\s*["']?(?!#|data:image\/(?:png|jpeg|webp);base64,)/i.test(svg);
}
